import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import EditHistoryLink from './EditHistoryLink';
import TypeAhead from '../TypeAhead';
import type { CustomDrawerProps } from './TablemaintPage';
import { getEdiOrder, updateEdiOrder, type EdiOrderRecord } from '../../services/ediorders.service';
import { isDeletedFlag } from './fields';
import { getEdiLocation, type EdiLocationRow } from '../../services/edilocations.service';
import EdiLocationsEditDrawer from './EdiLocationsEditDrawer';
import EdiEquipmentMapEditDrawer from './EdiEquipmentMapEditDrawer';
import EdiScacMapEditDrawer from './EdiScacMapEditDrawer';
import PortMapDrawer from '../PortMapDrawer';
import EdiFileActions from '../EdiFileActions';
import { renderMappedLocationLine, renderMappedSizeTypeLine, renderMappedPortLine, renderMappedScacLine } from '../../utils/ediOrderColumns';
import { ORDER_MOVE_TYPES } from '../../constants/orderFieldOptions';
import { titleCase } from '../../utils/titleCase';

/**
 * Bespoke edit drawer for EDI Orders (cms.edi_orders). Fetches the full denormalized
 * record on open (SCAC/broker/ports/hazmat/rate/… — more than the grid carries) and
 * applies the conditional edits (mirrors V1 editedi_orders):
 *   - pronumber SET  → editable but CLEAR-ONLY (never re-point to a different pro);
 *                      nothing else is editable.
 *   - pronumber UNSET → Move Type (dropdown); Template (clear-only, if set); Deleted
 *                      (Y needs a Delete Reason, N clears it); Clear Accept/Reject
 *                      (immediate action button, when accept/reject is filled).
 */
const TABLE = 'edi-orders';
const s = (v: any): string => (v == null ? '' : String(v));

/** A party name whose CMS record has since been soft-deleted. The EDI order keeps
 *  pointing at whatever it matched at receive time, so an old order can reference a
 *  deleted bill-to / customer / broker — flag it the way the grid does rather than
 *  showing a name that reads as live. */
const refValue = (name: any, deletedFlag: any): React.ReactNode => {
  const n = s(name);
  if (!n || !isDeletedFlag(deletedFlag)) return n;
  return (
    <span className="text-red-600 dark:text-red-400">
      {n}<span className="ml-1.5 text-[0.625rem] font-semibold">(deleted)</span>
    </span>
  );
};

const Field: React.FC<{ label: string; children?: React.ReactNode; wide?: boolean }> = ({ label, children, wide }) => (
  <div className={`flex flex-col${wide ? ' md:col-span-2' : ''}`}>
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg whitespace-pre-wrap break-words">
      {(children == null || children === '') ? <span className="text-gray-400">—</span> : children}
    </div>
  </div>
);

const EdiOrdersEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const recno = row ? Number(row.recno) : 0;

  const [rec, setRec] = useState<EdiOrderRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // Editable state (seeded from the fetched record).
  const [proInput, setProInput] = useState('');
  const [movetype, setMovetype] = useState('');
  const [tplInput, setTplInput] = useState('');
  const [formDeleted, setFormDeleted] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    if (!recno) return;
    setLoading(true);
    setLoadError(null);
    getEdiOrder(recno)
      .then((r) => {
        setRec(r);
        setProInput(r.pronumber ? String(r.pronumber) : '');
        setMovetype(s(r.movetype));
        setTplInput(s(r.template));
        setFormDeleted(s(r.deleted) === 'Y');
        setReason(s(r.deletereason));
      })
      .catch((e: any) => setLoadError(e?.response?.data?.detail || e?.message || 'Failed to load EDI order.'))
      .finally(() => setLoading(false));
  }, [recno]);

  useEffect(() => { load(); }, [load]);

  // Location mapping: a "Map Location" click fetches the edi_locations row and opens the
  // shared edit drawer. On save, refresh the grid (onSaved) and refetch this record in
  // place (load) so the mapped name updates without closing.
  const [mapRow, setMapRow] = useState<EdiLocationRow | null>(null);
  const openMap = useCallback((code: number) => {
    getEdiLocation(code).then(setMapRow).catch(() => { /* leave drawer as-is on lookup error */ });
  }, []);
  const [mapEquipRecno, setMapEquipRecno] = useState<number | null>(null);
  const openMapEquip = useCallback((recno: number) => setMapEquipRecno(recno), []);
  // Port mapping: opens the port-map drawer for that EDI unlocode (V1 edi_mapport).
  const [mapPortUnlocode, setMapPortUnlocode] = useState<string | null>(null);
  const openMapPort = useCallback((unlocode: string) => setMapPortUnlocode(unlocode), []);
  // SCAC mapping: opens the edi_scacmap editor for that recno.
  const [mapScacRecno, setMapScacRecno] = useState<number | null>(null);
  const openMapScac = useCallback((recno: number) => setMapScacRecno(recno), []);

  // Each location field: the EDI-supplied name, plus a mapped-CMS-location / "Map
  // Location" sub-line (blank slot → em-dash via <Field>).
  const renderLoc = (label: string, ediName: string, mapName: string, code: unknown) => {
    const nm = s(ediName);
    const mapLine = renderMappedLocationLine(mapName, code, openMap);
    return (
      <Field label={label}>
        {nm || mapLine ? (
          <div className="flex flex-col gap-0.5">
            {nm && <span>{nm}</span>}
            {mapLine}
          </div>
        ) : null}
      </Field>
    );
  };

  const proSet = !!rec && Number(rec.pronumber) !== 0;
  const templateSet = !!rec && s(rec.template) !== '';
  const arSet = !!rec && s(rec.acceptreject) !== '';
  const alreadyDeleted = !!rec && s(rec.deleted) === 'Y';

  const moveOptions = useMemo(() => {
    const base = [{ value: '', label: '— None —' }, ...ORDER_MOVE_TYPES.map((m) => ({ value: m, label: titleCase(m) }))];
    if (movetype && !base.some((o) => o.value === movetype)) base.push({ value: movetype, label: movetype });
    return base;
  }, [movetype]);

  const buildPatch = (): { patch: Record<string, any>; error?: string } => {
    if (!rec) return { patch: {} };
    const patch: Record<string, any> = {};
    if (proSet) {
      const v = proInput.trim();
      if (v !== String(rec.pronumber)) {
        if (v !== '') return { patch: {}, error: 'You can only remove the pro number here, not change it to a different one.' };
        patch.clear_pronumber = true;
      }
    } else {
      if (movetype !== s(rec.movetype)) patch.movetype = movetype;
      if (templateSet) {
        const v = tplInput.trim();
        if (v !== s(rec.template)) {
          if (v !== '') return { patch: {}, error: 'You can only remove the template here, not change it to a different one.' };
          patch.clear_template = true;
        }
      }
      const wasDeleted = s(rec.deleted) === 'Y';
      if (formDeleted !== wasDeleted || (formDeleted && reason !== s(rec.deletereason))) {
        if (formDeleted && !reason.trim()) return { patch: {}, error: 'A Delete Reason is required to mark this order deleted.' };
        patch.deleted = formDeleted ? 'Y' : 'N';
        if (formDeleted) patch.deletereason = reason;
      }
    }
    return { patch };
  };

  const handleSave = useCallback(async () => {
    const { patch, error } = buildPatch();
    if (error) { setSaveError(error); return; }
    if (Object.keys(patch).length === 0) { onClose(); return; }
    setSaveError(null);
    setIsSaving(true);
    try {
      await updateEdiOrder(recno, patch);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to update EDI order.');
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec, proSet, templateSet, proInput, movetype, tplInput, formDeleted, reason, recno, onClose, onSaved]);

  const handleClearAcceptReject = useCallback(async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      await updateEdiOrder(recno, { clear_accept_reject: true });
      onSaved();      // refresh the grid
      load();         // refresh the drawer in place (accept/reject now blank)
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to clear accept/reject.');
    } finally {
      setIsSaving(false);
    }
  }, [recno, onSaved, load]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving || loading || !rec}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> Save Changes</>)}
      </button>
    </div>
  );

  // Prefer the fetched record for the title so the drawer reads correctly no matter how
  // it's opened — e.g. embedded in the Pro history with only a { recno } row (detach an
  // EDI order by clearing its pronumber) — falling back to the passed row, then recno.
  const titlePro = rec ? (Number(rec.pronumber) ? String(rec.pronumber) : '') : (s(row?.pronumber) !== '0' ? s(row?.pronumber) : '');
  const titleCont = rec ? s(rec.container) : s(row?.container);
  const title = `EDI Order — ${titlePro ? `Pro #${titlePro}` : titleCont || `#${recno}`}`;

  return (
    <>
    <Drawer isOpen onClose={onClose} title={title} position="right" width="52rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {!!recno && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}

        {saveError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {saveError}
          </div>
        )}
        {loadError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {loadError}
          </div>
        )}

        {loading || !rec ? (
          <div className="flex items-center justify-center py-16 text-muted">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-3" /> Loading…
          </div>
        ) : (
          <>
            {/* Accept/Reject block + clear action (only when no pro attached). */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex items-start justify-between gap-3">
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted mb-1">Accept/Reject</span>
                <span className="text-sm text-gray-800 dark:text-gray-200">{s(rec.acceptrejectinfo) || <span className="text-gray-400">—</span>}</span>
              </div>
              {arSet && !proSet && (
                <button type="button" onClick={handleClearAcceptReject} disabled={isSaving}
                  className="shrink-0 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50">
                  Clear Accept/Reject
                </button>
              )}
            </div>

            {/* Editable section — what the page actually lets you change. */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 p-4 space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Editable</h3>
              {proSet ? (
                <div>
                  <label className="block text-sm font-medium text-label mb-1">Pro Number</label>
                  <input type="text" value={proInput} disabled={isSaving}
                    onChange={(e) => setProInput(e.target.value)}
                    className="w-48 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  <p className="mt-1 text-xs text-muted">
                    Clear the pro number to detach this EDI order. It can’t be re-pointed to a different pro here.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Move Type</label>
                    <TypeAhead options={moveOptions} value={movetype} onChange={(v) => setMovetype((v as string) || '')}
                      onClear={() => setMovetype('')} placeholder="Select…" disabled={isSaving} showSearch />
                  </div>
                  {templateSet && (
                    <div>
                      <label className="block text-sm font-medium text-label mb-1">Template</label>
                      <input type="text" value={tplInput} disabled={isSaving}
                        onChange={(e) => setTplInput(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                      <p className="mt-1 text-xs text-muted">Clear-only — can’t be changed to a different template.</p>
                    </div>
                  )}
                  <div className="md:col-span-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    {/* Undelete is not allowed (Lee 2026-08-06): once deleted, the checkbox
                        locks checked and the reason is read-only. Backend enforces it too. */}
                    <label className={`flex items-center gap-3 select-none ${alreadyDeleted ? '' : 'cursor-pointer'}`}>
                      <input type="checkbox" checked={formDeleted} disabled={isSaving || alreadyDeleted}
                        onChange={(e) => setFormDeleted(e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60" />
                      <span className="text-sm font-medium text-label">Deleted</span>
                    </label>
                    {alreadyDeleted && (
                      <p className="mt-1 text-xs text-muted">A deleted EDI order can’t be undeleted here.</p>
                    )}
                    {formDeleted && (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Delete Reason <span className="text-red-500">*</span></label>
                        <textarea value={reason} disabled={isSaving || alreadyDeleted} rows={3}
                          onChange={(e) => setReason(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-70" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Read-only detail. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Bill To">{refValue(rec.billtoname, rec.billtodeleted)}</Field>
              <Field label="Customer">{refValue(rec.customername, rec.custdeleted)}</Field>
              {proSet && <Field label="Pro Number">{s(rec.pronumber)}</Field>}
              <Field label="Container">{s(rec.container)}</Field>
              <Field label="SCAC">{renderMappedScacLine(rec.scac, rec.mappedscac, openMapScac)}</Field>
              {proSet && <Field label="Move Type">{s(rec.movetype)}</Field>}
              <Field label="Broker">{refValue(rec.brokername, rec.brokerdeleted)}</Field>
              <Field label="PO">{s(rec.po)}</Field>
              <Field label="BOL">{s(rec.bol)}</Field>
              <Field label="Booking">{s(rec.booking)}</Field>
              <Field label="Bill To Ref #">{s(rec.billtorefno)}</Field>
              <Field label="Seal Number">{s(rec.sealnumber)}</Field>
              <Field label="Port of Origin">{renderMappedPortLine(rec.portoforiginname, rec.portoforiginunlocode, openMapPort)}</Field>
              <Field label="Port of Discharge">{renderMappedPortLine(rec.portofdischargename, rec.portofdischargeunlocode, openMapPort)}</Field>
              <Field label="Vessel/Voyage">{s(rec.vesselvoyage)}</Field>
              <Field label="Voyage">{s(rec.voyage)}</Field>
              <Field label="Lbs/Kgs">{s(rec.lbskgs)}</Field>
              <Field label="Hazmat">{s(rec.hazmat)}</Field>
              <Field label="Hazmat Info">{s(rec.hazmatinfo)}</Field>
              <Field label="Size/Type">{renderMappedSizeTypeLine(rec.sizetype, rec.equipmentmaprecno, openMapEquip)}</Field>
              {renderLoc('Pickup', rec.pickupediname, rec.pickupmapname, rec.pickupcode)}
              {renderLoc('Deliver To', rec.deliveryediname, rec.deliverymapname, rec.deliverycode)}
              {renderLoc('Terminate', rec.terminateediname, rec.terminatemapname, rec.terminatecode)}
              <Field label="Rate Info" wide>{s(rec.rateinfo)}</Field>
              {proSet && templateSet && <Field label="Template">{s(rec.template)}</Field>}
              <Field label="Comments" wide>{s(rec.comments)}</Field>
              <Field label="Notes" wide>{s(rec.nte)}</Field>
              <Field label="Commodity">{s(rec.lad)}</Field>
              <Field label="DUNS Number/Shipper">{s(rec.shippercode)}</Field>
              <Field label="SAP Order Number/Customer Reference">{s(rec.sapordernumber)}</Field>
              <Field label="EDI Type">{s(rec.editype)}</Field>
              <Field label="Receive Date">{s(rec.processdatetime)}</Field>
              <Field label="EDI File" wide>{s(rec.edifilename)}</Field>
              <Field label="NAS EDI File" wide>
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span className="break-all">{s(rec.nas_edifilename) || <span className="text-gray-400">—</span>}</span>
                  <EdiFileActions recno={rec.recno} nasEdifilename={rec.nas_edifilename} />
                </span>
              </Field>
              {proSet && <Field label="Delete Reason" wide>{s(rec.deletereason)}</Field>}
            </div>
          </>
        )}
      </div>
    </Drawer>

    {/* Location mapping — the shared EDI Locations edit drawer, opened from a
        "Map Location" line. On save, refresh the grid and refetch this record so the
        mapped name updates in place. Portals to <body>, so it stacks above this drawer. */}
    {mapRow && (
      <EdiLocationsEditDrawer
        row={mapRow}
        onClose={() => setMapRow(null)}
        onSaved={() => { onSaved(); load(); }}
      />
    )}
    {mapEquipRecno != null && (
      <EdiEquipmentMapEditDrawer
        recno={mapEquipRecno}
        onClose={() => setMapEquipRecno(null)}
        onSaved={() => { onSaved(); load(); }}
      />
    )}
    {mapPortUnlocode && (
      <PortMapDrawer
        unlocode={mapPortUnlocode}
        onClose={() => setMapPortUnlocode(null)}
        onSaved={() => { onSaved(); load(); }}
      />
    )}
    {mapScacRecno != null && (
      <EdiScacMapEditDrawer
        recno={mapScacRecno}
        onClose={() => setMapScacRecno(null)}
        onSaved={() => { onSaved(); load(); }}
      />
    )}
    </>
  );
};

export default EdiOrdersEditDrawer;
